import React from 'react';
import { DomainArea } from '../../../config/domainSelectorAgents';
import {
  AgentDomainSelector as AgentDomainSelector__Root,
  AgentDomainSelector__Label,
  AgentDomainSelector__Buttons,
  AgentDomainSelector__DomainBtn,
  AgentDomainSelector__ActiveTag,
  AgentDomainSelector__ActiveTagDot,
  AgentDomainSelector__BlockedNotice,
} from './AgentDomainSelector.styles';

const DEFAULT_DOMAINS: readonly DomainArea[] = [];

/**
 * Appends the selected domain tag to the end of a user message so the
 * back-end can route the query to the correct domain knowledge base.
 *
 * Example output:
 *   "What is the status?\n\ntag: FS"
 */
export const appendDomainTag = (message: string, domain: DomainArea): string =>
  `${message}\n\ntag: ${domain}`;

export interface AgentDomainSelectorProps {
  /** Currently selected domain, or null if none chosen yet. */
  selectedDomain: DomainArea | null;
  /** Called when the user picks a domain. */
  onDomainSelect: (domain: DomainArea) => void;
  /** Domain choices to render. */
  availableDomains?: readonly DomainArea[];
  /** Domain choices currently disabled in UI. */
  disabledDomains?: readonly DomainArea[];
  /**
   * When true the component also renders a notice reminding the user to
   * select a domain before sending a message.
   */
  showBlockedNotice?: boolean;
}

export const AgentDomainSelector: React.FC<AgentDomainSelectorProps> = ({
  selectedDomain,
  onDomainSelect,
  availableDomains = DEFAULT_DOMAINS,
  disabledDomains = [],
  showBlockedNotice = false,
}) => {
  const disabledSet = new Set(disabledDomains);

  return (
    <AgentDomainSelector__Root>
      <AgentDomainSelector__Label>
        Select a domain area to get started
      </AgentDomainSelector__Label>

      <AgentDomainSelector__Buttons>
        {availableDomains.map((domain) => {
          const isDisabled = disabledSet.has(domain);
          return (
            <AgentDomainSelector__DomainBtn
              key={domain}
              $active={selectedDomain === domain}
              $disabled={isDisabled}
              onClick={() => !isDisabled && onDomainSelect(domain)}
              aria-pressed={selectedDomain === domain}
              aria-disabled={isDisabled}
              title={isDisabled ? `${domain} is currently unavailable` : undefined}
            >
              {domain}
            </AgentDomainSelector__DomainBtn>
          );
        })}
      </AgentDomainSelector__Buttons>

      {selectedDomain && (
        <AgentDomainSelector__ActiveTag>
          <AgentDomainSelector__ActiveTagDot />
          Domain area: <strong>{selectedDomain}</strong> — all your messages
          will be tagged automatically
        </AgentDomainSelector__ActiveTag>
      )}

      {showBlockedNotice && !selectedDomain && (
        <AgentDomainSelector__BlockedNotice>
          Please select a domain area above before sending a message.
        </AgentDomainSelector__BlockedNotice>
      )}
    </AgentDomainSelector__Root>
  );
};
